import { useEffect } from 'react';
import { CopyIcon, ArrowForwardIcon } from '@rsos/assets/icons';
import { Text } from '@rsos/base-components';
import { Loader } from '@rsos/base-components/Button/Loader';
import { ButtonText } from '@rsos/components/capstone/Location/styles/common.styles';
import { Button, Accordion } from '@rsos/components/capstone/base';
import type { StatusDisplayItem } from '@rsos/reduxDataStore/src/features/resourceRequests';
import { useTheme } from '@rsos/theme/styled';
import copyToClipboard from '@rsos/utils/copyToClipboard';
import {
  trackStatusPageRender,
  trackOpenTransportLink,
  trackCopyTrackingLink,
} from '../../../gainsightTrackings/airAmbulanceTrackings';
import { FullWidthContent } from '../../JurisdictionView/EventPane/tabs/OverviewTab/OverviewTab.styles';
import { STATUS_PAGE_STATES } from '../AirAmbulanceForm/constants';
import {
  BannerWrapper,
  IconWrapper,
  BannerText,
} from '../Banner/StatusBanner.styles';
import {
  StyledLink,
  FlexRow,
  MarginWrapper,
  VerticalSpacing,
  FlexRowSpaceBetween,
  FullWidthDiv,
} from './AirAmbulanceEtlStatus.styles';
import AirAmbulanceEtlStatusControls from './AirAmbulanceEtlStatusControls';
import useAirAmbulanceEtlStatus from './hooks/useAirAmbulanceEtlStatus';

/**
 * Main status display component for ETL-based Air Ambulance requests.
 */
function AirAmbulanceEtlStatus(): JSX.Element {
  const {
    transport,
    selectedRequestId,
    isLoading,
    statusPageState,
    latestStatus,
    remainingStatuses,
    brandTail,
    trackingUrl,
    formattedStatuses,
    pickupAddress,
    destination,
    latestDispatchCenter,
  } = useAirAmbulanceEtlStatus();

  // Determine if we should show tracking component
  const hasTrackingData = brandTail || trackingUrl;
  const hasAnyStatuses = formattedStatuses.length > 0;
  const shouldShowTracking =
    statusPageState !== STATUS_PAGE_STATES.SUBMITTED ||
    hasTrackingData ||
    hasAnyStatuses;

  return (
    <div>
      <MarginWrapper>
        <EtlStatusBanner
          statusPageState={statusPageState}
          latestStatusDisplay={latestStatus?.display}
        />
      </MarginWrapper>

      <EtlTransportDetails
        pickupAddress={pickupAddress}
        destination={destination}
        dispatchCenterName={latestDispatchCenter?.name}
        dispatchCenterPhone={latestDispatchCenter?.phoneNumber}
      />

      {shouldShowTracking && (
        <EtlTrackingInfo
          brandTail={brandTail}
          trackingUrl={trackingUrl}
          requestId={selectedRequestId}
        />
      )}

      <EtlStatusUpdates
        latestStatus={latestStatus}
        remainingStatuses={remainingStatuses}
        requestId={selectedRequestId}
        isLoading={isLoading}
      />

      {transport && <AirAmbulanceEtlStatusControls transport={transport} />}
    </div>
  );
}

function formatTime(timestamp: string | undefined): string {
  if (!timestamp) return 'N/A';
  try {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return 'N/A';
  }
}

interface EtlStatusBannerProps {
  statusPageState: (typeof STATUS_PAGE_STATES)[keyof typeof STATUS_PAGE_STATES];
  latestStatusDisplay?: string;
}

function EtlStatusBanner({
  statusPageState,
  latestStatusDisplay,
}: EtlStatusBannerProps): JSX.Element {
  const IconComponent = statusPageState.icon;
  const displayText = latestStatusDisplay
    ? `Air ambulance status: ${latestStatusDisplay}`
    : statusPageState.message;

  return (
    <BannerWrapper bgColor={statusPageState.bgColor} horizontal>
      <IconWrapper>
        <IconComponent color="background" height={16} width={16} />
      </IconWrapper>
      <BannerText>{displayText}</BannerText>
    </BannerWrapper>
  );
}

interface EtlTransportDetailsProps {
  pickupAddress: string | null;
  destination: string | null;
  dispatchCenterName: string | null | undefined;
  dispatchCenterPhone: string | null | undefined;
}

function EtlTransportDetails({
  pickupAddress,
  destination,
  dispatchCenterName,
  dispatchCenterPhone,
}: EtlTransportDetailsProps): JSX.Element | null {
  if (
    !pickupAddress &&
    !destination &&
    !dispatchCenterName &&
    !dispatchCenterPhone
  ) {
    return null;
  }

  return (
    <MarginWrapper>
      {pickupAddress && (
        <VerticalSpacing>
          <Text color="secondaryText" size="normal">
            Pickup
          </Text>
          <Text color="primaryText" size="normal">
            {pickupAddress}
          </Text>
        </VerticalSpacing>
      )}
      {destination && (
        <VerticalSpacing>
          <Text color="secondaryText" size="normal">
            Destination
          </Text>
          <Text color="primaryText" size="normal">
            {destination}
          </Text>
        </VerticalSpacing>
      )}
      {dispatchCenterName && (
        <VerticalSpacing>
          <Text color="secondaryText" size="normal">
            Air Dispatch
          </Text>
          <Text color="primaryText" size="normal">
            {dispatchCenterName}
          </Text>
        </VerticalSpacing>
      )}
      {dispatchCenterPhone && (
        <VerticalSpacing>
          <Text color="secondaryText" size="normal">
            Air Dispatch Phone
          </Text>
          <Text color="primaryText" size="normal">
            {dispatchCenterPhone}
          </Text>
        </VerticalSpacing>
      )}
    </MarginWrapper>
  );
}

interface EtlTrackingInfoProps {
  /** @see AirAmbulanceEtlStatusResult.brandTail */
  brandTail: string | null;
  /** @see AirAmbulanceEtlStatusResult.trackingUrl */
  trackingUrl: string | null;
  requestId: string | null;
}

function EtlTrackingInfo({
  brandTail,
  trackingUrl,
  requestId,
}: EtlTrackingInfoProps): JSX.Element | null {
  const handleCopyLink = () => {
    if (trackingUrl) {
      copyToClipboard(trackingUrl);
    }
    trackCopyTrackingLink({
      airAmbulanceUrl: trackingUrl || undefined,
      requestId: requestId || undefined,
    });
  };

  const handleTrackAirAmbulance = () => {
    if (trackingUrl) {
      window.open(trackingUrl, '_blank', 'noopener,noreferrer');
    }
    trackOpenTransportLink({
      airAmbulanceUrl: trackingUrl || undefined,
      requestId: requestId || undefined,
    });
  };

  if (!brandTail && !trackingUrl) {
    return null;
  }

  return (
    <>
      <MarginWrapper>
        {brandTail && (
          <VerticalSpacing>
            <Text color="secondaryText" size="normal">
              Aircraft Name - Tail Number
            </Text>
            <Text color="primaryText" size="normal">
              {brandTail}
            </Text>
          </VerticalSpacing>
        )}
        {trackingUrl && (
          <VerticalSpacing>
            <Text color="secondaryText" size="normal">
              Tracking URL
            </Text>
            <Text size="normal">
              <StyledLink
                href={trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {trackingUrl}
              </StyledLink>
            </Text>
          </VerticalSpacing>
        )}
      </MarginWrapper>
      {trackingUrl && (
        <FlexRowSpaceBetween>
          <Button
            type="button"
            grow
            btnType="tertiary"
            onClick={handleCopyLink}
          >
            <CopyIcon /> <ButtonText>Copy Link</ButtonText>
          </Button>
          <Button
            grow
            btnType="tertiary"
            type="button"
            onClick={handleTrackAirAmbulance}
          >
            <ArrowForwardIcon /> <ButtonText>Track Air Ambulance</ButtonText>
          </Button>
        </FlexRowSpaceBetween>
      )}
    </>
  );
}

interface StatusItemProps {
  status: StatusDisplayItem;
  index: number;
}

function StatusItem({ status, index }: StatusItemProps): JSX.Element {
  return (
    <FlexRow key={`${status.timestamp || index}-${index}`}>
      <Text color="primaryBase" size="normal">
        {formatTime(status.timestamp)}
      </Text>
      <Text ml={2} color="secondaryText" size="normal">
        {status.display}
      </Text>
    </FlexRow>
  );
}

interface StatusUpdateHeaderProps {
  latestStatus: StatusDisplayItem | null;
  isLoading: boolean;
}

function StatusUpdateHeader({
  latestStatus,
  isLoading,
}: StatusUpdateHeaderProps): JSX.Element {
  const theme = useTheme();

  return (
    <div>
      <Text mb={2} color="primaryText" fontWeight="bold" size="normal">
        Status Updates
      </Text>
      <FlexRow>
        {latestStatus?.display ? (
          <>
            <Text color="primaryBase" size="normal">
              {formatTime(latestStatus?.timestamp)}
            </Text>
            <Text color="secondaryText" size="normal">
              {latestStatus?.display}
            </Text>
          </>
        ) : (
          <>
            <Text color="secondaryText" size="normal">
              Awaiting status updates...
            </Text>
            {isLoading && (
              <Loader size={16} color={theme.colors.secondaryText} />
            )}
          </>
        )}
      </FlexRow>
    </div>
  );
}

interface RemainingStatusesListProps {
  statuses: StatusDisplayItem[];
}

function RemainingStatusesList({
  statuses,
}: RemainingStatusesListProps): JSX.Element {
  return (
    <>
      {statuses
        .filter(status => status.display && status.timestamp)
        .map((status, index) => (
          <StatusItem
            key={`${status.timestamp || index}-${index}`}
            status={status}
            index={index}
          />
        ))}
    </>
  );
}

interface EtlStatusUpdatesProps {
  latestStatus: StatusDisplayItem | null;
  remainingStatuses: StatusDisplayItem[];
  requestId: string | null;
  isLoading: boolean;
}

function EtlStatusUpdates({
  latestStatus,
  remainingStatuses,
  requestId,
  isLoading,
}: EtlStatusUpdatesProps): JSX.Element {
  // Track the latest status update only when tripId or status changes
  useEffect(() => {
    const statusDisplay = latestStatus?.display;
    if (statusDisplay && requestId) {
      trackStatusPageRender({
        requestId: requestId,
        status: statusDisplay,
      });
    }
  }, [requestId, latestStatus?.display]);

  return (
    <MarginWrapper style={{ marginTop: '16px', marginBottom: '16px' }}>
      <FullWidthContent>
        <div>
          <FlexRowSpaceBetween style={{ marginTop: 0, marginBottom: 0 }}>
            <FullWidthDiv>
              {remainingStatuses.length > 0 ? (
                <Accordion
                  header={
                    <StatusUpdateHeader
                      latestStatus={latestStatus}
                      isLoading={isLoading}
                    />
                  }
                  headerIcon="chevron"
                  interactionType="button"
                  noBorder
                  width="100%"
                  headerPadding="0"
                  padding="0"
                  content={
                    <RemainingStatusesList statuses={remainingStatuses} />
                  }
                />
              ) : (
                <StatusUpdateHeader
                  latestStatus={latestStatus}
                  isLoading={isLoading}
                />
              )}
            </FullWidthDiv>
          </FlexRowSpaceBetween>
        </div>
      </FullWidthContent>
    </MarginWrapper>
  );
}

export default AirAmbulanceEtlStatus;
